import { Mp3Encoder } from '@breezystack/lamejs'

/** Anything above this is refused rather than uploaded. */
export const MAX_BYTES = 100 * 1024 * 1024 // 100 MB

/**
 * Formats that are already compressed. Re-encoding these would cost time and
 * quality for almost no size saving, so they pass straight through.
 */
const ALREADY_COMPRESSED = /^(audio\/(mpeg|mp4|aac|ogg|opus|amr|webm|x-m4a)|video\/)/

/** Uncompressed audio — the formats worth re-encoding. */
const RAW_AUDIO = /^(audio\/(wav|x-wav|wave|aiff|x-aiff|x-caf|flac|basic|l16))/

export const isCompressible = (type = '', name = '') =>
  RAW_AUDIO.test(type) || /\.(wav|aiff|aif|caf|flac)$/i.test(name)

const KBPS = 128        // plenty for a demo a pastor listens to once
const TARGET_RATE = 44100

/**
 * Re-encode uncompressed audio to MP3 in the browser, before it ever goes near
 * the network.
 *
 * A studio bounce is often a 30 MB WAV; the same take at 128 kbps is under 3 MB.
 * That is the difference between an upload that feels broken and one that
 * finishes while the writer is still reading the next question — and it costs
 * nothing in review quality, because nobody is mastering from this file.
 *
 * Runs faster than realtime (decode + encode, no playback), and reports
 * progress so the UI can show it working.
 */
export async function compressAudio(file, onProgress) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)()
  try {
    const decoded = await ctx.decodeAudioData(await file.arrayBuffer())

    // Mono is right for a voice memo or a guitar/vocal demo, and halves the
    // size again. Stereo is kept if the source genuinely uses both channels.
    const channels = Math.min(decoded.numberOfChannels, 2)
    const encoder = new Mp3Encoder(channels, decoded.sampleRate || TARGET_RATE, KBPS)

    const left = decoded.getChannelData(0)
    const right = channels > 1 ? decoded.getChannelData(1) : null

    // lamejs wants 16-bit PCM
    const toPcm = (f32, start, len) => {
      const out = new Int16Array(len)
      for (let i = 0; i < len; i++) {
        const s = Math.max(-1, Math.min(1, f32[start + i]))
        out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
      return out
    }

    const BLOCK = 1152 // one MP3 frame
    const chunks = []
    for (let i = 0; i < left.length; i += BLOCK) {
      const len = Math.min(BLOCK, left.length - i)
      const l = toPcm(left, i, len)
      const r = right ? toPcm(right, i, len) : null
      const buf = r ? encoder.encodeBuffer(l, r) : encoder.encodeBuffer(l)
      if (buf.length > 0) chunks.push(buf)

      // Yield to the main thread so the page stays responsive on a phone.
      if (onProgress && (i / BLOCK) % 400 === 0) {
        onProgress(Math.round((i / left.length) * 100))
        await new Promise((r2) => setTimeout(r2, 0))
      }
    }
    const last = encoder.flush()
    if (last.length > 0) chunks.push(last)
    onProgress?.(100)

    const blob = new Blob(chunks.map((c) => new Uint8Array(c)), { type: 'audio/mpeg' })

    // If re-encoding somehow made it bigger, keep the original.
    if (blob.size >= file.size) return { file, compressed: false }

    const name = file.name.replace(/\.[^.]+$/, '') + '.mp3'
    return {
      file: new File([blob], name, { type: 'audio/mpeg' }),
      compressed: true,
      from: file.size,
      to: blob.size,
    }
  } finally {
    ctx.close?.()
  }
}

export const mb = (n) => (n >= 1024 * 1024 * 1024
  ? `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`
  : n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB`
  : `${Math.max(1, Math.round(n / 1024))} KB`)

/** Plain guidance rather than a bare rejection. */
export function tooBigMessage(file) {
  const video = (file.type || '').startsWith('video/')
  return video
    ? `That video is ${mb(file.size)} and the limit is 100 MB. Trim it to the part that matters, ` +
      `record at 720p instead of 4K, or send just the audio — a pastor is listening more than watching.`
    : `That file is ${mb(file.size)} and the limit is 100 MB. Export it as an MP3 or M4A rather than ` +
      `a full-quality WAV and it will be a fraction of the size.`
}
